import { Cookies } from 'react-cookie';
import { refreshAccessToken } from '@/api/auth.api';
import type { Token } from '@/interfaces/service/auth.interface';
import { regexEmail, regexNotNumber, regexPhoneNumber } from '@/constants/regex.constants';
import { clearUserInfo } from '@/utils/user.util';

const cookies = new Cookies();
// Token refresh queue management
let isRefreshing = false;
let refreshSubscribers: ((token: Token | null) => void)[] = [];

// Token management
export const setToken = (token: Token): void => {
  if (!token) {
    return;
  }

  const serialized = JSON.stringify(token);

  cookies.set('token', serialized, {
    path: '/',
    maxAge: 604800, // 1주일
    domain: process.env.NEXT_PUBLIC_DOMAIN,
    secure: true,
    sameSite: 'strict'
  });
};

export const getToken = (): Token | null => {
  const cookieValue = cookies.get('token');
  if (cookieValue) {
    return cookieValue as Token;
  }
  return null;
};

export const clearToken = () => {
  const domain = '.dev.medilawyer.co.kr';
  cookies.remove('token', { path: '/', domain });
  cookies.remove('role', { path: '/', domain });
};

export const clearAuthorization = () => {
  const domain = process.env.NEXT_PUBLIC_DOMAIN;
  cookies.remove('token', {
    path: '/',
    ...(domain && domain !== '/' && !domain.includes('/') && { domain })
  });
  cookies.remove('role', {
    path: '/',
    ...(domain && domain !== '/' && !domain.includes('/') && { domain })
  });
  clearToken();
  clearUserInfo();
};

// Token refresh handling
const onTokenRefreshed = (cb: (token: Token | null) => void): void => {
  refreshSubscribers.push(cb);
};

const resolveRefreshToken = (token: Token | null): void => {
  refreshSubscribers.forEach((cb) => cb(token));
  refreshSubscribers = [];
};

export const handleTokenRefresh = async (): Promise<Token | null> => {
  if (!isRefreshing) {
    isRefreshing = true;
    try {
      const token = await refreshAccessToken();
      resolveRefreshToken(token || null);
      return token || null;
    } catch (error) {
      resolveRefreshToken(null);
      return null;
    } finally {
      isRefreshing = false;
    }
  }

  return new Promise((resolve) => {
    onTokenRefreshed((token) => {
      resolve(token);
    });
  });
};

export const checkValidationEmail = (email: string | undefined) => email !== undefined && email !== null && regexEmail.test(email);

export const checkValidationPhone = (phoneNumber: string | undefined) => {
  if (phoneNumber === undefined || phoneNumber === null) {
    return false;
  }
  return new RegExp(regexPhoneNumber, 'g').test(phoneNumber);
};

export const checkValidationOtp = (otp: string | undefined) => otp !== undefined && otp !== null && !regexNotNumber.test(otp) && otp.length === 6;

export const hasClientToken = (): boolean => {
  if (typeof window === 'undefined') return false;
  return !!getToken();
};
